import {
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from "@angular/core";
import { finalize, Subject, takeUntil } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { SharedModule } from "../../../../shared/shared.module";
import {
  Contact,
  ContactReference,
  ContactSource,
  ExtensionContact,
  ManualContact,
} from "../../../interfaces/contact.interface";
import { ContactUtils } from "../../../utils/contact-utils";
import {
  IAddressBookDialogData,
  IAddressBookDialogResult,
  IManualContactCreateData,
  IManualContactDeleteData,
  IManualContactDeleteResult,
  IManualContactEditData,
  IManualContactResult,
} from "../../../interfaces/contact-widget.interface";
import { ContactWidgetService } from "../../../services/contact-widget.service";
import { ManualContactCreateComponent } from "./manual-contact-create/manual-contact-create.component";
import { ManualContactEditComponent } from "./manual-contact-edit/manual-contact-edit.component";
import { ManualContactDeleteComponent } from "./manual-contact-delete/manual-contact-delete.component";

@Component({
  selector: "lib-address-book",
  imports: [SharedModule],
  templateUrl: "./address-book.component.html",
  styleUrl: "./address-book.component.scss",
})
export class AddressBookDialogComponent implements OnInit, OnDestroy {
  private readonly contactWidgetService: ContactWidgetService =
    inject(ContactWidgetService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly translate: TranslateService = inject(TranslateService);

  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  // Data signals
  availableContacts: WritableSignal<Contact[]> = signal([]);
  selectedContactIds: WritableSignal<Set<string>> = signal(new Set());
  isLoading: WritableSignal<boolean> = signal(false);

  // Search and filtering
  searchTerm: string = "";

  // Sorting - only alphabetical for address book
  sortBy: WritableSignal<"default" | "alphabetical"> = signal("default");

  // Track already selected contacts
  alreadySelectedCount: number = 0;

  // Track manual contact changes
  private readonly manualContactChanges = {
    created: [] as ManualContact[],
    updated: [] as ManualContact[],
    deleted: [] as string[],
  };

  // Expose ContactUtils for template
  protected readonly ContactUtils = ContactUtils;
  protected readonly ContactSource = ContactSource;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IAddressBookDialogData,
    public dialogRef: MatDialogRef<AddressBookDialogComponent>,
  ) {
    this.alreadySelectedCount =
      data.alreadySelectedContactIds?.filter(
        (contactRef: ContactReference) => contactRef.source === data.source,
      ).length || 0;
  }

  ngOnInit(): void {
    this.loadContacts();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private loadContacts(): void {
    this.isLoading.set(true);

    switch (this.data.source) {
      case ContactSource.MANUAL:
        this.loadManualContacts();
        break;
      case ContactSource.EXTENSION:
        this.loadExtensionContacts();
        break;
      default:
        console.warn(
          `[AddressBookDialog] Unsupported contact source: ${this.data.source}`,
        );
        this.isLoading.set(false);
    }
  }

  private loadManualContacts(): void {
    // Get manual contacts from existing contacts or widget data
    const manualContacts = this.data.existingContacts.filter(
      (contact: Contact): contact is ManualContact =>
        ContactUtils.isManualContact(contact),
    );

    this.availableContacts.set(manualContacts);
    this.isLoading.set(false);
  }

  private loadExtensionContacts(): void {
    if (!this.data.widget?._id) {
      console.error(
        "[AddressBookDialog] Widget ID is required for extension contacts",
      );
      this.isLoading.set(false);
      return;
    }

    this.contactWidgetService
      .requestAllExtensionContacts(this.data.widget._id)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: (extensionContacts: ExtensionContact[]) => {
          console.log(
            `[AddressBookDialog] Loaded ${extensionContacts.length} extension contacts`,
          );
          this.availableContacts.set(extensionContacts);
        },
        error: (error) => {
          console.error(
            "[AddressBookDialog] Error loading extension contacts:",
            error,
          );
          this.snackBar.open(
            this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.FAILED_TO_LOAD_EXTENSION_CONTACTS",
            ),
            "Close",
            { duration: 3000 },
          );
        },
      });
  }

  // Sorting method
  setSortBy(sortType: "default" | "alphabetical"): void {
    const previousSort = this.sortBy();
    this.sortBy.set(sortType);

    // Provide user feedback about the sorting change
    if (previousSort !== sortType && this.availableContacts().length > 0) {
      const message =
        sortType === "alphabetical"
          ? this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.CONTACTS_SORTED_ALPHABETICALLY",
            )
          : this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.CONTACTS_SORTED_BY_SELECTION_ORDER",
            );

      this.snackBar.open(message, "", { duration: 1500 });
    }
  }

  filteredAndSortedContacts(): Contact[] {
    let contacts = this.availableContacts();

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      contacts = contacts.filter(
        (contact) =>
          contact.name.toLowerCase().includes(searchLower) ||
          ContactUtils.getDisplayPhoneNumber(contact)
            .toLowerCase()
            .includes(searchLower),
      );
    }

    // Apply sorting
    if (this.sortBy() === "alphabetical") {
      contacts = [...contacts].sort((a, b) => a.name.localeCompare(b.name));
    }

    return contacts;
  }

  // Update the isContactSelected method to handle already selected contacts
  isContactSelected(contact: Contact): boolean {
    const contactKey = this.getContactKey(contact);
    const isInCurrentSelection = this.selectedContactIds().has(contactKey);
    const isAlreadyInGroup = this.isAlreadySelected(contact);

    if (isAlreadyInGroup) {
      // If already in group, selection state is inverted
      // - Not in current selection = still selected (default state)
      // - In current selection = marked for removal (deselected)
      return !isInCurrentSelection;
    } else {
      // Normal behavior for new contacts
      return isInCurrentSelection;
    }
  }

  isAlreadySelected(contact: Contact): boolean {
    return (
      this.data.alreadySelectedContactIds?.some(
        (ref: ContactReference) =>
          ref.contactId === contact.contactId && ref.source === contact.source,
      ) || false
    );
  }

  toggleContact(contact: Contact): void {
    const contactKey = this.getContactKey(contact);
    const currentSelected = new Set(this.selectedContactIds());

    // Check if contact is already selected in the group (from parent)
    const isAlreadyInGroup =
      this.data.alreadySelectedContactIds?.some(
        (ref: ContactReference) =>
          ref.contactId === contact.contactId && ref.source === contact.source,
      ) || false;

    if (isAlreadyInGroup) {
      // If contact is already in the group, remove it from the group selection
      // We need to track removals separately since we can't modify the original alreadySelectedContactIds
      if (currentSelected.has(contactKey)) {
        // Contact was re-selected after being in group, now deselecting
        currentSelected.delete(contactKey);
      } else {
        // Contact is in group but not in current selection, add to removal tracking
        currentSelected.add(contactKey);
      }
    } else {
      // Normal toggle behavior for contacts not already in group
      if (currentSelected.has(contactKey)) {
        currentSelected.delete(contactKey);
      } else {
        currentSelected.add(contactKey);
      }
    }

    this.selectedContactIds.set(currentSelected);
  }

  // Update the selectAllContacts method
  selectAllContacts(): void {
    const currentSelected = new Set(this.selectedContactIds());

    this.filteredAndSortedContacts().forEach((contact) => {
      const contactKey = this.getContactKey(contact);
      const isAlreadyInGroup = this.isAlreadySelected(contact);

      if (isAlreadyInGroup) {
        // For already selected contacts, remove them from current selection
        // (which means they stay selected in the group)
        currentSelected.delete(contactKey);
      } else {
        // For new contacts, add them to selection
        currentSelected.add(contactKey);
      }
    });

    this.selectedContactIds.set(currentSelected);
  }

  // Update the clearAllSelections method
  clearAllSelections(): void {
    const currentSelected = new Set<string>();

    // For already selected contacts, add them to current selection
    // (which marks them for removal/deselection)
    this.filteredAndSortedContacts().forEach((contact) => {
      const isAlreadyInGroup = this.isAlreadySelected(contact);
      if (isAlreadyInGroup) {
        const contactKey = this.getContactKey(contact);
        currentSelected.add(contactKey);
      }
    });

    this.selectedContactIds.set(currentSelected);
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
  }

  // Count contacts to be added (new contacts that are selected)
  getContactsToAddCount(): number {
    return this.availableContacts().filter((contact) => {
      const contactKey = this.getContactKey(contact);
      const isInCurrentSelection = this.selectedContactIds().has(contactKey);
      const isAlreadyInGroup = this.isAlreadySelected(contact);

      return !isAlreadyInGroup && isInCurrentSelection;
    }).length;
  }

  // Count contacts to be removed (already selected contacts that are marked for removal)
  getContactsToRemoveCount(): number {
    return this.availableContacts().filter((contact) => {
      const contactKey = this.getContactKey(contact);
      const isInCurrentSelection = this.selectedContactIds().has(contactKey);
      const isAlreadyInGroup = this.isAlreadySelected(contact);

      return isAlreadyInGroup && isInCurrentSelection;
    }).length;
  }

  // Check if there are any changes to apply
  hasChangesToApply(): boolean {
    const addCount = this.getContactsToAddCount();
    const removeCount = this.getContactsToRemoveCount();
    return addCount > 0 || removeCount > 0;
  }

  // Manual contact management methods
  createManualContact(): void {
    const data = {
      widget: this.data.widget,
    } as IManualContactCreateData;

    const dialogRef = this.dialog.open(ManualContactCreateComponent, {
      panelClass: ["astimax-manual-contact-create-dialog", "adx-dialog-medium"],
      disableClose: true,
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IManualContactResult | string) => {
        if (result && typeof result === "object" && result.contact) {
          if (result.success) {
            // Use the actual contact from the result instead of creating a new one
            const newContact = result.contact;

            // Add to available contacts
            this.availableContacts.update((contacts: Contact[]) => [
              ...contacts,
              newContact,
            ]);

            // Automatically select the new contact
            const contactKey = this.getContactKey(newContact);
            this.selectedContactIds.update(
              (selected: Set<string>) => new Set([...selected, contactKey]),
            );

            // Track the created contact for parent component
            this.manualContactChanges.created.push(newContact);

            this.snackBar.open(
              `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACT")} "${newContact.name}" ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CREATED_SUCCESSFULLY")}`,
              "Close",
              { duration: 3000 },
            );
          } else {
            this.snackBar.open(
              this.translate.instant(
                "ASTIMAX.CONTACT.WIDGET.FAILED_TO_CREATE_CONTACT",
              ),
              "Close",
              { duration: 3000 },
            );
          }
        }
      });
  }

  editManualContact(contact: ManualContact): void {
    const data = {
      widget: this.data.widget,
      contact: contact,
    } as IManualContactEditData;

    const dialogRef = this.dialog.open(ManualContactEditComponent, {
      panelClass: ["astimax-manual-contact-edit-dialog", "adx-dialog-medium"],
      disableClose: true,
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IManualContactResult | string) => {
        if (result && typeof result === "object" && result.contact) {
          if (result.success) {
            // Use the actual contact from the result
            const updatedContact = result.contact;

            // Update in available contacts
            this.availableContacts.update((contacts: Contact[]) =>
              contacts.map((c: Contact) =>
                c.contactId === contact.contactId ? updatedContact : c,
              ),
            );

            // Track the updated contact for parent component
            this.manualContactChanges.updated.push(updatedContact);

            this.snackBar.open(
              `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACT")} "${updatedContact.name}" ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.UPDATE_SUCCESSFULLY")}`,
              "Close",
              { duration: 3000 },
            );
          } else {
            this.snackBar.open(
              this.translate.instant(
                "ASTIMAX.CONTACT.WIDGET.FAILED_TO_UPDATE_CONTACT",
              ),
              "Close",
              { duration: 3000 },
            );
          }
        }
      });
  }

  deleteManualContact(contact: ManualContact): void {
    const data = {
      widget: this.data.widget,
      contact: contact,
      groupsContainingContact: [], // Could be populated if needed
    } as IManualContactDeleteData;

    const dialogRef = this.dialog.open(ManualContactDeleteComponent, {
      panelClass: ["astimax-manual-contact-delete-dialog", "adx-dialog-small"],
      disableClose: true,
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IManualContactDeleteResult | string) => {
        if (result && typeof result === "object") {
          if (result.success) {
            // Remove from available contacts
            this.availableContacts.update((contacts: Contact[]) =>
              contacts.filter(
                (c: Contact) => c.contactId !== contact.contactId,
              ),
            );

            // Remove from selection if selected
            const contactKey = this.getContactKey(contact);
            this.selectedContactIds.update((selected: Set<string>) => {
              const newSelected = new Set(selected);
              newSelected.delete(contactKey);
              return newSelected;
            });

            // Track the deleted contact for parent component
            this.manualContactChanges.deleted.push(contact.contactId);

            this.snackBar.open(
              `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACT")}t "${contact.name}" ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.DELETE_SUCCESSFULLY")}`,
              "Close",
              { duration: 3000 },
            );
          } else {
            this.snackBar.open(
              `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACT")} "${contact.name}" ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.FAILED_TO_DELETE_CONTACT")}`,
              "Close",
              { duration: 3000 },
            );
          }
        }
      });
  }

  // Helper methods
  private getContactKey(contact: Contact): string {
    return `${contact.contactId}-${contact.source}`;
  }

  getContactAriaLabel(contact: Contact): string {
    let label = `${contact.name}, ${ContactUtils.getDisplayPhoneNumber(contact)}`;

    const isAlreadyInGroup = this.isAlreadySelected(contact);
    const isCurrentlySelected = this.isContactSelected(contact);

    if (isAlreadyInGroup && isCurrentlySelected) {
      label += ", currently in group";
    } else if (isAlreadyInGroup && !isCurrentlySelected) {
      label += ", marked for removal from group";
    } else if (!isAlreadyInGroup && isCurrentlySelected) {
      label += ", selected for addition to group";
    } else {
      label += ", not selected";
    }

    if (ContactUtils.isExtensionContact(contact)) {
      const status = ContactUtils.getExtensionStatus(contact);
      label += `, status: ${ContactUtils.getStatusText(status)}`;
    }

    return label;
  }

  getSourceTitle(): string {
    switch (this.data.source) {
      case ContactSource.MANUAL:
        return this.translate.instant("ASTIMAX.CONTACT.WIDGET.MANUAL_CONTACTS");
      case ContactSource.EXTENSION:
        return this.translate.instant(
          "ASTIMAX.CONTACT.WIDGET.EXTENSION_CONTACTS",
        );
      default:
        return this.translate.instant(
          "ASTIMAX.CONTACT.WIDGET.UNKNOWN_CONTACTS",
        );
    }
  }

  getSourceDescription(): string {
    return this.translate.instant(
      "ASTIMAX.CONTACT.WIDGET.SELECT_CONTACTS_TO_BE_ASSIGNED_TO_THE_CONTACT_GROUP",
    );
  }

  getSelectionIndicatorDescription(indicator?: string): string {
    switch (indicator) {
      case "alreadySelected":
        return this.translate.instant(
          "ASTIMAX.CONTACT.WIDGET.ALREADY_SELECTED",
        );
      case "selected":
        return this.translate.instant("ASTIMAX.CONTACT.WIDGET.SELECTED");
      case "deselected":
        return this.translate.instant("ASTIMAX.CONTACT.WIDGET.DESELECTED");
      default:
        return this.translate.instant("ASTIMAX.CONTACT.WIDGET.CLICK_TO_SELECT");
    }
  }

  getEmptyStateMessage(): string {
    switch (this.data.source) {
      case ContactSource.MANUAL:
        return this.translate.instant(
          "ASTIMAX.CONTACT.WIDGET.HELP_TEXT_MANUAL_CONTACT",
        );
      case ContactSource.EXTENSION:
        return this.translate.instant(
          "ASTIMAX.CONTACT.WIDGET.HELP_TEXT_EXTENSION_CONTACT",
        );
      default:
        return this.translate.instant(
          "ASTIMAX.CONTACT.WIDGET.HELP_TEXT_NO_CONTACT",
        );
    }
  }

  // Update the confirm method to handle removals
  confirm(): void {
    const contactsToAdd: Contact[] = [];
    const contactsToRemove: ContactReference[] = [];

    this.availableContacts().forEach((contact) => {
      const contactKey = this.getContactKey(contact);
      const isInCurrentSelection = this.selectedContactIds().has(contactKey);
      const isAlreadyInGroup = this.isAlreadySelected(contact);

      if (isAlreadyInGroup && isInCurrentSelection) {
        // Already in group + in current selection = marked for removal
        contactsToRemove.push({
          contactId: contact.contactId,
          source: contact.source!,
          order: 0, // Order will be handled by parent
        });
      } else if (!isAlreadyInGroup && isInCurrentSelection) {
        // Not in group + in current selection = new addition
        contactsToAdd.push(contact);
      }
    });

    const result: IAddressBookDialogResult = {
      selectedContacts: contactsToAdd,
      contactsToRemove: contactsToRemove, // Add this new property
      manualContactChanges:
        this.manualContactChanges.created.length > 0 ||
        this.manualContactChanges.updated.length > 0 ||
        this.manualContactChanges.deleted.length > 0
          ? this.manualContactChanges
          : undefined,
    };

    this.dialogRef.close(result);
  }

  cancel(): void {
    // Always return manual contact changes, even when canceling
    // This ensures that edit/delete operations are communicated back to parent
    const hasManualChanges =
      this.manualContactChanges.created.length > 0 ||
      this.manualContactChanges.updated.length > 0 ||
      this.manualContactChanges.deleted.length > 0;

    if (hasManualChanges) {
      const result: IAddressBookDialogResult = {
        selectedContacts: [], // No contacts selected when canceling
        manualContactChanges: this.manualContactChanges,
      };
      this.dialogRef.close(result);
    } else {
      this.dialogRef.close();
    }
  }
}
